import React, { useState, useEffect, useRef } from 'react';
import { db } from './firebase';
import { 
  doc, 
  getDoc, 
  collection, 
  updateDoc, 
  increment,
  query,
  where,
  getDocs,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import { useParams, useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';

const functions = getFunctions();
const submitRatingFn = httpsCallable(functions, 'submitRating');

const isDevelopment = process.env.NODE_ENV === 'development';

const generateFingerprint = async () => {
  const components = [];
  components.push(`screen:${window.screen.width}x${window.screen.height}`);
  components.push(`colorDepth:${window.screen.colorDepth}`);
  components.push(`pixelRatio:${window.devicePixelRatio}`);
  components.push(`timezone:${Intl.DateTimeFormat().resolvedOptions().timeZone}`);
  components.push(`language:${navigator.language}`);
  components.push(`platform:${navigator.platform}`);
  components.push(`hardwareConcurrency:${navigator.hardwareConcurrency || 'unknown'}`);
  const ua = navigator.userAgent;
  components.push(`mobile:${/Mobile|Android|iPhone/i.test(ua)}`);
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 200; canvas.height = 50;
    ctx.textBaseline = 'top'; ctx.font = '14px Arial';
    ctx.fillStyle = '#f60'; ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069'; ctx.fillText('Fingerprint', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)'; ctx.fillText('Fingerprint', 4, 17);
    components.push(`canvas:${canvas.toDataURL().length}`);
  } catch { components.push('canvas:error'); }
  try {
    const gl = document.createElement('canvas').getContext('webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      components.push(`webglVendor:${gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)?.substring(0, 20) || 'unknown'}`);
      components.push(`webglRenderer:${gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)?.substring(0, 20) || 'unknown'}`);
    }
  } catch { components.push('webgl:error'); }
  const str = components.join('|');
  let hash = 0;
  for (let i = 0; i < str.length; i++) { const c = str.charCodeAt(i); hash = ((hash << 5) - hash) + c; hash = hash & hash; }
  return Math.abs(hash).toString(36);
};

const isInstagramApp = () => {
  if (isDevelopment) return true;
  const ua = navigator.userAgent.toLowerCase();
  return /instagram/i.test(ua) || (/instagram.*applewebkit/i.test(ua) && !/safari/i.test(ua)) || /instagram.*android/i.test(ua);
};

const Spinner = () => (
  <div style={{ width: '40px', height: '40px', border: '3px solid rgba(255,255,255,0.3)', borderTop: '3px solid #FFF', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
);

const StarRating = ({ rating }) => {
  const fullStars = Math.round(rating);
  return (
    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
      {[1,2,3,4,5].map(star => (
        <span key={star} style={{ fontSize: '26px', color: star <= fullStars ? '#FFD700' : 'rgba(255,255,255,0.25)' }}>★</span>
      ))}
    </div>
  );
};

const RatingPage = () => {
  const { affiliateId, linkId } = useParams();
  const navigate = useNavigate();

  const [linkData, setLinkData] = useState(null);
  const [affiliateFirstName, setAffiliateFirstName] = useState(null);
  const [affiliateProfilePic, setAffiliateProfilePic] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasAlreadyVoted, setHasAlreadyVoted] = useState(false);
  const [pageOpenTracked, setPageOpenTracked] = useState(false);
  const [fingerprint, setFingerprint] = useState(null);
  const [isValidEnvironment, setIsValidEnvironment] = useState(true);

  const [profileImageLoading, setProfileImageLoading] = useState(true);
  const [backgroundImageLoading, setBackgroundImageLoading] = useState(true);

  const [spins, setSpins] = useState([]);
  const [spinsRemaining, setSpinsRemaining] = useState(3);
  const [isSpinning, setIsSpinning] = useState(false);
  const [hasStartedSpinning, setHasStartedSpinning] = useState(false);
  const [selectedRatingIndex, setSelectedRatingIndex] = useState(null);

  const [showThankYou, setShowThankYou] = useState(false);
  const [averageRating, setAverageRating] = useState(null);
  const [ratingCount, setRatingCount] = useState(null);

  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  useEffect(() => {
    const update = () => setViewportHeight(window.visualViewport ? window.visualViewport.height : window.innerHeight);
    window.visualViewport?.addEventListener('resize', update);
    window.addEventListener('resize', update);
    update();
    return () => { window.visualViewport?.removeEventListener('resize', update); window.removeEventListener('resize', update); };
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.webkitTouchCallout = 'none';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.webkitTouchCallout = '';
    };
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        if (!isInstagramApp() && !isDevelopment) {
          setIsValidEnvironment(false);
          setError('Please open this link in the Instagram app to rate stories');
          setLoading(false);
          return;
        }
        if (isDevelopment) {
          setFingerprint(`dev_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
          return;
        }
        const fp = await generateFingerprint();
        setFingerprint(fp);
        localStorage.setItem(`rating_fingerprint_${linkId}`, fp);
      } catch {
        const fallback = `fallback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        setFingerprint(fallback);
        localStorage.setItem(`rating_fingerprint_${linkId}`, fallback);
      }
    };
    init();
  }, [linkId]);

  useEffect(() => {
    const loadData = async () => {
      if (!isValidEnvironment || !fingerprint) { setLoading(false); return; }

      try {
        const linksQuery = query(collection(db, 'rating_links'), where('linkId', '==', linkId));
        const linkSnapshot = await getDocs(linksQuery);

        if (linkSnapshot.empty) { setError('Rating link not found or expired'); setLoading(false); return; }

        const linkDoc = linkSnapshot.docs[0];
        const data = { id: linkDoc.id, ...linkDoc.data() };
        setLinkData(data);
        if (data.photoUrl) setPhotoUrl(data.photoUrl);

        const affiliateDoc = await getDoc(doc(db, 'affiliates', affiliateId));
        if (affiliateDoc.exists()) {
          const ad = affiliateDoc.data();
          if (ad.firstName) setAffiliateFirstName(ad.firstName);
          if (ad.profilePictureUrl) setAffiliateProfilePic(ad.profilePictureUrl);
        }

        if (!isDevelopment) {
          const existingQuery = query(
            collection(db, 'ratings'),
            where('linkId', '==', data.id),
            where('fingerprint', '==', fingerprint)
          );
          const existing = await getDocs(existingQuery);
          if (!existing.empty) {
            await loadAverageRating(data.id, null);
            setHasAlreadyVoted(true);
            setShowThankYou(true);
          }
        }

        trackUniquePageOpen(data.id, fingerprint);
        await checkAndLoadSpinState(data.id, fingerprint);

      } catch (e) {
        console.error('Error loading data:', e);
        setError('Failed to load rating page');
      }

      setLoading(false);
    };

    if (affiliateId && linkId && fingerprint && isValidEnvironment) loadData();
  }, [affiliateId, linkId, fingerprint, isValidEnvironment]);

  const loadAverageRating = async (linkDocId, justSubmittedRating) => {
    try {
      const ratingsQuery = query(collection(db, 'ratings'), where('linkId', '==', linkDocId));
      const ratingsSnap = await getDocs(ratingsQuery);
      const ratings = ratingsSnap.docs.map(d => d.data().rating).filter(r => typeof r === 'number');
      if (justSubmittedRating !== null && justSubmittedRating !== undefined) ratings.push(justSubmittedRating);
      if (ratings.length === 0) {
        setAverageRating(justSubmittedRating ?? 0);
        setRatingCount(justSubmittedRating !== null ? 1 : 0);
      } else {
        const avg = ratings.reduce((a, b) => a + b, 0) / ratings.length;
        setAverageRating(Math.round(avg * 10) / 10);
        setRatingCount(ratings.length);
      }
    } catch (e) {
      console.error('Error loading average rating:', e);
      if (justSubmittedRating !== null) { setAverageRating(justSubmittedRating); setRatingCount(1); }
    }
  };

  const trackUniquePageOpen = async (linkDocId, userFingerprint) => {
    if (pageOpenTracked) return;
    setPageOpenTracked(true);
    try {
      const trackingRef = doc(db, 'unique_page_opens', `${linkDocId}_${userFingerprint}`);
      const trackingDoc = await getDoc(trackingRef);
      if (!trackingDoc.exists()) {
        await setDoc(trackingRef, { linkId: linkDocId, fingerprint: userFingerprint, firstOpenedAt: serverTimestamp(), openCount: 1 });
        await updateDoc(doc(db, 'rating_links', linkDocId), { totalPageOpens: increment(1), lastOpenedAt: serverTimestamp() });
      } else {
        await updateDoc(trackingRef, { openCount: increment(1), lastOpenedAt: serverTimestamp() });
      }
    } catch (e) { console.error('Error tracking page open:', e); }
  };

  const checkAndLoadSpinState = async (linkDocId, userFingerprint) => {
    if (isDevelopment) return false;
    try {
      const spinStateDoc = await getDoc(doc(db, 'spin_states', `${linkDocId}_${userFingerprint}`));
      if (spinStateDoc.exists()) {
        const data = spinStateDoc.data();
        const savedSpins = data.spins || [];
        setSpins(savedSpins);
        const spinsUsed = savedSpins.filter(s => s !== undefined && s !== null).length;
        setSpinsRemaining(3 - spinsUsed);
        if (spinsUsed > 0) setHasStartedSpinning(true);
        return true;
      }
      return false;
    } catch (e) { console.error('Error checking spin state:', e); return false; }
  };

  const saveSpinState = async (linkDocId, userFingerprint, spinsArray) => {
    if (isDevelopment) return;
    try {
      await setDoc(doc(db, 'spin_states', `${linkDocId}_${userFingerprint}`), {
        linkId: linkDocId, fingerprint: userFingerprint, spins: spinsArray,
        createdAt: serverTimestamp(), lastUpdatedAt: serverTimestamp()
      });
    } catch (e) { console.error('Error saving spin state:', e); }
  };

  const runSingleSpin = (slotIndex, usedRatings, forcedRating = null) => {
    return new Promise((resolve) => {
      let spinCount = 0;
      const spinInterval = setInterval(() => {
        setSpins(prev => { const n = [...prev]; n[slotIndex] = Math.floor(Math.random() * 5) + 1; return n; });
        spinCount++;
        if (spinCount >= 10) {
          clearInterval(spinInterval);
          let finalRating;
          if (forcedRating !== null) {
            finalRating = forcedRating;
          } else {
            let attempts = 0;
            do { finalRating = Math.floor(Math.random() * 5) + 1; attempts++; }
            while (usedRatings.includes(finalRating) && attempts < 100);
          }
          setSpins(prev => { const n = [...prev]; n[slotIndex] = finalRating; return n; });
          resolve({ slotIndex, finalRating });
        }
      }, 100);
    });
  };

  const handleSpin = async () => {
    if (isSpinning || spinsRemaining <= 0 || hasAlreadyVoted) return;
    setIsSpinning(true);
    setHasStartedSpinning(true);

    const currentSpins = [...spins];
    const settledRatings = currentSpins.filter(r => r !== undefined && r !== null);
    const totalToSpin = spinsRemaining;
    const slotOffset = 3 - spinsRemaining;

    const guaranteedRating = Math.random() < 0.5 ? 4 : 5;
    const guaranteedSlot = slotOffset + Math.floor(Math.random() * totalToSpin);
    const usedRatings = [...settledRatings, guaranteedRating];

    for (let i = 0; i < totalToSpin; i++) {
      const slotIndex = slotOffset + i;
      const isGuaranteed = slotIndex === guaranteedSlot;
      const { finalRating } = await runSingleSpin(slotIndex, isGuaranteed ? [] : usedRatings, isGuaranteed ? guaranteedRating : null);
      if (!isGuaranteed) usedRatings.push(finalRating);
      settledRatings.push(finalRating);
      currentSpins[slotIndex] = finalRating;
      setSpinsRemaining(prev => prev - 1);
      if (i < totalToSpin - 1) await new Promise(r => setTimeout(r, 600));
    }

    if (linkData && fingerprint) saveSpinState(linkData.id, fingerprint, currentSpins);
    setIsSpinning(false);
  };

  const handleSelectRating = async (index, rating) => {
    if (selectedRatingIndex !== null || hasAlreadyVoted) return;
    setSelectedRatingIndex(index);

    try {
      await submitRatingFn({ linkId: linkData.linkId, affiliateId, stars: rating, fingerprint });
      await loadAverageRating(linkData.id, rating);
      setShowThankYou(true);
      setHasAlreadyVoted(true);
    } catch (e) {
      console.error('Error submitting rating:', e);
      setSelectedRatingIndex(null);
      alert('Failed to submit rating. Please try again.');
    }
  };

  const handleContinue = async () => {
    // Track unique continue click per fingerprint per link
    if (linkData?.id && fingerprint) {
      try {
        const trackingRef = doc(db, 'unique_continue_clicks', `${linkData.id}_${fingerprint}`);
        const trackingDoc = await getDoc(trackingRef);
        if (!trackingDoc.exists()) {
          await setDoc(trackingRef, {
            linkId: linkData.id,
            fingerprint,
            firstClickedAt: serverTimestamp(),
            clickCount: 1
          });
          await updateDoc(doc(db, 'rating_links', linkData.id), {
            totalContinueClicks: increment(1),
            lastContinueClickAt: serverTimestamp()
          });
        } else {
          await updateDoc(trackingRef, {
            clickCount: increment(1),
            lastClickedAt: serverTimestamp()
          });
        }
      } catch (e) { console.error('Error tracking continue click:', e); }
    }
    navigate(`/bonus/${affiliateId}/${linkId}`);
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#10183C' }}>
      <Spinner />
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (!isValidEnvironment) return (
    <div style={{ minHeight: '100vh', backgroundColor: '#10183C', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ textAlign: 'center', width: '100%', maxWidth: '500px' }}>
        <div style={{ backgroundColor: '#1A2245', borderRadius: '12px', padding: '40px 30px' }}>
          <p style={{ color: 'rgba(255,255,255,0.8)', fontWeight: '600', fontSize: '18px', lineHeight: '1.5' }}>{error}</p>
        </div>
      </div>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, height: `${viewportHeight}px`, backgroundColor: '#10183C', overflow: 'hidden' }}>
      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Background photo */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 1, backgroundColor: '#10183C' }}>
        {backgroundImageLoading && photoUrl && (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.1)' }}>
            <Spinner />
          </div>
        )}
        {photoUrl && (
          <img src={photoUrl} alt="Rating"
            onLoad={() => setBackgroundImageLoading(false)}
            onError={() => setBackgroundImageLoading(false)}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: backgroundImageLoading ? 'none' : 'block' }}
          />
        )}
      </div>

      {/* Nav bar */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, padding: '20px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {affiliateProfilePic && (
            <div style={{ position: 'relative', width: '35px', height: '35px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
              {profileImageLoading && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.2)' }}>
                  <div style={{ width: '15px', height: '15px', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #FFF', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                </div>
              )}
              <img src={affiliateProfilePic} alt={affiliateFirstName}
                onLoad={() => setProfileImageLoading(false)}
                onError={() => setProfileImageLoading(false)}
                style={{ width: '35px', height: '35px', borderRadius: '50%', objectFit: 'cover', display: profileImageLoading ? 'none' : 'block' }}
              />
            </div>
          )}
          <span style={{ color: 'white', fontSize: '19px', fontWeight: 'bold', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {affiliateFirstName}
          </span>
        </div>
      </div>

      {/* Thank you screen */}
      {showThankYou && (
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: '#1A2245', borderTopLeftRadius: '20px', borderTopRightRadius: '20px', zIndex: 30, padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', animation: 'fadeUp 0.4s ease' }}>
          <div style={{ fontSize: '22px', fontWeight: 'bold', color: 'white' }}>
            Thanks for rating!
          </div>

          {averageRating !== null && (
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
              <div style={{ fontSize: '15px', color: 'rgba(255,255,255,0.5)', fontWeight: '500' }}>
                Average Rating
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '34px', fontWeight: 'bold', color: '#FFD700' }}>{averageRating.toFixed(1)}</span>
                <StarRating rating={averageRating} />
              </div>
              <div style={{ fontSize: '15px', color: 'rgba(255,255,255,0.5)', fontWeight: '500' }}>
                {ratingCount} {ratingCount === 1 ? 'rater' : 'raters'}
              </div>
            </div>
          )}

          <button
            onClick={handleContinue}
            style={{
              width: '100%',
              height: '58px',
              backgroundColor: '#4169E1',
              border: 'none',
              borderRadius: '200px',
              color: 'white',
              fontSize: '20px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.2s ease'
            }}
            onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.98)'; }}
            onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            Continue
          </button>
        </div>
      )}

      {/* Slot machine footer */}
      {!showThankYou && (
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: '#1A2245', borderTopLeftRadius: '20px', borderTopRightRadius: '20px', zIndex: 30, padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px', pointerEvents: selectedRatingIndex !== null ? 'none' : 'auto' }}>
          <div style={{ textAlign: 'center', color: 'white', fontSize: '17px', fontWeight: '600' }}>
            {!hasStartedSpinning && 'Tap spin'}
            {hasStartedSpinning && spinsRemaining > 0 && 'Spinning...'}
            {spinsRemaining === 0 && 'Pick a rating'}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', minHeight: '70px', alignItems: 'center' }}>
            {[0, 1, 2].map((index) => (
              <button
                key={index}
                onClick={() => spinsRemaining === 0 && spins[index] && handleSelectRating(index, spins[index])}
                disabled={spinsRemaining > 0 || selectedRatingIndex !== null || !spins[index]}
                style={{
                  width: '60px', height: '60px',
                  borderRadius: '5px',
                  backgroundColor: '#2A3A6B',
                  border: 'none',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  cursor: spinsRemaining === 0 && spins[index] && selectedRatingIndex === null ? 'pointer' : 'default',
                  transition: 'all 0.3s ease',
                  transform: selectedRatingIndex === index ? 'scale(1.1)' : 'scale(1)',
                  opacity: spins[index] ? 1 : 0.3
                }}
              >
                {spins[index] && (
                  <>
                    <span style={{ fontSize: '24px', fontWeight: 'bold', color: 'white' }}>{spins[index]}</span>
                    <span style={{ fontSize: '16px', color: 'white' }}>★</span>
                  </>
                )}
              </button>
            ))}
          </div>

          <button
            onClick={handleSpin}
            disabled={isSpinning || spinsRemaining === 0}
            style={{
              width: '100%', height: '58px',
              backgroundColor: spinsRemaining > 0 && !isSpinning ? '#4169E1' : '#666',
              border: 'none', borderRadius: '200px',
              color: spinsRemaining > 0 && !isSpinning ? '#FFF' : '#c2c2c2',
              fontSize: '20px', fontWeight: 'bold',
              cursor: spinsRemaining > 0 && !isSpinning ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'transform 0.2s ease'
            }}
            onMouseDown={e => { if (spinsRemaining > 0 && !isSpinning) e.currentTarget.style.transform = 'scale(0.98)'; }}
            onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            {isSpinning ? 'Spinning...' : 'Spin'}
          </button>
        </div>
      )}
    </div>
  );
};

export default RatingPage;